import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule, ClientSideRowModelModule, CsvExportModule } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Toast } from "../../../config/Toast";
import { FormatSalaryItem } from "../../../helper/ResponseSalary";
import { ColumnSalaryCalculation } from "./ColumnSalaryCalculation";
import "flatpickr/dist/themes/light.css";
import moment from "moment";

import Button from "../../../components/Button";
import Badge from "../../../components/Badge";
import Pagination from "../../../components/Pagination";
import SalaryCalculationModals from "./Modals/SalaryCalculationModals";
import SalaryCalculationControls from "./SalaryCalculationControls";

// Services
import SalaryCalculationService from "../../../services/SalaryCalculation";
import BonusPointService from "../../../services/BonusPoint";
import PriceSkillService from "../../../services/PriceSkill";
import BookmarkSalaryService from "../../../services/BookmarkSalary";

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule, CsvExportModule]);

const notify = {
  success: (msg) => toast.success(msg, Toast),
  error: (msg) => toast.error(msg, Toast),
  info: (msg) => toast.info(msg, Toast),
};

const limitDataPages = 10;

const SalaryCalculation = () => {
  const [rowData, setRowData] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPosting, setLoadingPosting] = useState(false);
  const [loadingRecalculate, setLoadingRecalculate] = useState(false);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [rangeDate, setRangeDate] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState(null);
  const [totalPage, setTotalPage] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const moduleBookmark = "salary";

  const [payloadFilterProject, setPayloadFilterProject] = useState(null);
  const [payloadFilterSession, setPayloadFilterSession] = useState(null);
  const [payloadSort, setPayloadSort] = useState({ sort_by: null, sort_type: null });

  const [modalState, setModalState] = useState({ open: false, type: null, data: null });

  const openModal = useCallback((type, data = null) => setModalState({ open: true, type, data }), []);
  const closeModal = useCallback(() => setModalState({ open: false, type: null, data: null }), []);
  const closePostingRefundModal = useCallback(() => {
    closeModal();
    setLoadingPosting(false);
    setLoadingRefund(false);
  }, [closeModal]);

  const fetchSalaryCalculation = useCallback(
    async (opts = {}) => {
      if (!rangeDate[0] || !rangeDate[1]) return;

      setLoading(true);

      try {
        const params = {
          page: opts.page || pageNumber,
          limit: opts.limit || limitDataPages,
          q: opts.search ?? search,
          start: moment(rangeDate[0]).format("YYYY-MM-DD"),
          end: moment(rangeDate[1]).format("YYYY-MM-DD"),
          sort_by: opts.sort_by || payloadSort?.sort_by,
          sort_type: opts.sort_type || payloadSort?.sort_type,
          source_project: payloadFilterProject,
          sesi: payloadFilterSession,
        };

        const { data, total_page = 0 } =
          (
            await SalaryCalculationService.salaryCalculation(
              params.page,
              params.limit,
              params.q,
              params.start,
              params.end,
              params.sort_by,
              params.sort_type,
              params.source_project,
              params.sesi
            )
          )?.data || {};

        setRowData(FormatSalaryItem(data || [], params.page, params.limit));
        setTotalPage(total_page);
      } catch (err) {
        notify.error(err?.response?.data?.message || "Gagal memuat data Salary Calculation");
      } finally {
        setTimeout(() => setLoading(false), 400);
      }
    },
    [rangeDate, pageNumber, search, payloadFilterSession, payloadFilterProject, payloadSort]
  );

  useEffect(() => {
    if (rangeDate[0] && rangeDate[1]) fetchSalaryCalculation({ page: pageNumber });
  }, [rangeDate, pageNumber, search, payloadSort, payloadFilterSession, payloadFilterProject, fetchSalaryCalculation]);

  useEffect(() => {
    let mounted = true;
    BonusPointService.allWorkers()
      .then((res) => mounted && setWorkers(res?.data?.data || []))
      .catch((err) => notify.error(err?.response?.data?.message || "Gagal memuat daftar pekerja"));
    PriceSkillService.getSkills()
      .then((res) => {
        if (!mounted) return;
        const formatted = (res?.data?.data || []).map((s) => ({ value: s.id, label: s.name }));
        setSkills(formatted);
      })
      .catch((err) => notify.error(err?.response?.data?.message || "Gagal memuat daftar skill"));
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggleBookmark = useCallback(
    async (id, currentBookmark, bookMarkValue) => {
      setRowData((prev) =>
        prev.map((r) => {
          if (r.id === id) {
            const newBookmark = r.isBookmark === 1 ? 0 : 1;
            return { ...r, isBookmark: newBookmark };
          }
          return r;
        })
      );

      try {
        await BookmarkSalaryService.bookmark(id, bookMarkValue === 1 ? 0 : 1, moduleBookmark);
        notify.success("Berhasil memperbarui bookmark");
      } catch (err) {
        setRowData((prev) => prev.map((r) => (r.id === id ? { ...r, bookmark: currentBookmark } : r)));
        notify.error(err?.response?.data?.message || "Gagal memperbarui bookmark");
      } finally {
        fetchSalaryCalculation({ page: pageNumber });
      }
    },
    [fetchSalaryCalculation, pageNumber]
  );

  const handleSearch = useCallback((val) => {
    setSearch(val);
  }, []);

  const handleDateChange = useCallback((dates) => {
    if (Array.isArray(dates) && dates.length === 2) {
      notify.success(`Tanggal terpilih: ${moment(dates[0]).format("DD MMM")} - ${moment(dates[1]).format("DD MMM")}`, Toast);
      setRangeDate(dates);
      setPageNumber(1);
    }
  }, []);

  const handleOpenMenuModal = useCallback((row) => setModalState({ open: true, type: "menu", data: row }), []);

  const ActionRenderer = useCallback(
    (params) => {
      const row = params.data;
      const isPosting = row.isPosting === 1;
      const hasBookmarkBtn = !!row.id;
      const bookMarkValue = row.isBookmark;
      const bookmark = row.isBookmark === 1 ? "btn-success" : "btn-outline-success";

      return (
        <>
          {!isPosting && hasBookmarkBtn && (
            <Fragment>
              <Button
                button={`${bookmark} btn-sm me-1`}
                icon={`fa fa-bookmark ${bookmark === "btn-outline-success" ? "text-success" : ""}`}
                onClick={() => handleToggleBookmark(row.id, bookmark, bookMarkValue)}
              />
              <Button
                button="btn-outline-warning btn-sm"
                icon="fa fa-wrench text-warning"
                onClick={() => handleOpenMenuModal(row)}
              />
            </Fragment>
          )}
          {isPosting && <Badge badge="bg-primary pt-1 white-color" name="posted" type="rounded-pill" />}
        </>
      );
    },
    [handleToggleBookmark, handleOpenMenuModal]
  );

  const colDefs = useMemo(
    () =>
      ColumnSalaryCalculation(
        ActionRenderer,
        (payload) => setPayloadSort(payload),
        openDropdown,
        setOpenDropdown,
        { project: payloadFilterProject, session: payloadFilterSession },
        (payload) => {
          setPageNumber(1);
          setPayloadFilterProject(payload);
        },
        (payload) => {
          setPageNumber(1);
          setPayloadFilterSession(payload);
        }
      ),
    [ActionRenderer, openDropdown]
  );

  const handleChangePage = useCallback(({ selected }) => setPageNumber(selected + 1), []);

  return (
    <Fragment>
      <SalaryCalculationModals
        modalState={modalState}
        openModal={openModal}
        closeModal={closeModal}
        closePostingRefundModal={closePostingRefundModal}
        fetchSalaryCalculation={fetchSalaryCalculation}
        pageNumber={pageNumber}
        workers={workers}
        skills={skills}
        rangeDate={rangeDate}
        setLoadingPosting={setLoadingPosting}
        setLoadingRefund={setLoadingRefund}
      />

      <div className="d-flex flex-column justify-content-end align-items-center w-100 h-100 p-0 m-0 gap-4">
        <SalaryCalculationControls
          rangeDate={rangeDate}
          onDateChange={handleDateChange}
          loadingRecalculate={loadingRecalculate}
          loadingPosting={loadingPosting}
          loadingRefund={loadingRefund}
          setLoadingRecalculate={setLoadingRecalculate}
          setModalState={setModalState}
          handleSearch={handleSearch}
          handleGetList={fetchSalaryCalculation}
        />

        <div className="flex flex-row w-full p-0 m-0 z-[9999]">
          <div className="w-full p-0 m-0">
            <div
              className="ag-theme-alpine w-100 relative"
              style={{
                height: 50 * rowData.length + 50 + "px",
                maxHeight: 50 * rowData.length + 50 + "px",
                minHeight: "250px",
                overflow: "auto",
              }}
            >
              <AgGridReact
                rowData={loading ? [] : rowData}
                columnDefs={colDefs}
                domLayout="normal"
                theme="legacy"
                rowHeight={50}
                headerHeight={50}
                animateRows={true}
                enableCellTextSelection={true}
                sideBar={false}
                defaultColDef={{ sortable: true, filter: false, resizable: true }}
                overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Memuat data...</span>'}
                overlayNoRowsTemplate={
                  '<span style="display: none !important;" class="ag-overlay-loading-center">Tidak ada data</span>'
                }
                getRowStyle={(params) => {
                  const { isPosting, isModif } = params.data || {};

                  if (isPosting) {
                    switch (isPosting) {
                      case 1:
                        return { color: "#7041c1" };
                      case 0:
                        return { color: "#000000" };
                      default:
                        return { backgroundColor: "transparent" };
                    }
                  }

                  switch (isModif) {
                    case "INSERT":
                      return { color: "#348358" };
                    case "EDIT":
                      return { color: "#f5c10c" };
                    case "DELETE":
                      return { color: "#da2f3f" };
                    default:
                      return { backgroundColor: "transparent" };
                  }
                }}
              />
              {rowData.length === 0 && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                  <span className="ag-overlay-loading-center">Tidak ada data</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {totalPage > 0 && <Pagination pageCount={totalPage} forcePage={pageNumber - 1} changePage={handleChangePage} />}
      </div>
    </Fragment>
  );
};

export default SalaryCalculation;
